import { useState, useEffect } from 'react';
import { buscarLivroDoMes } from '../utils/livros';
import { useLivros } from '../hooks/useLivros';
import { useBadges } from '../hooks/useBadges';

const IconBook = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const IconBookOpen = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

const IconCheck = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconStar = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const IconExternalLink = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const IconBookFallback = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

export default function LivroDoMes({ user }) {
  const [livro, setLivro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgErro, setImgErro] = useState(false);

  const {
    stats,
    loadingLivros,
    fetchLivros,
    getStatusLivro,
    iniciarLeitura,
    marcarComoLido,
  } = useLivros(user?.id);

  const { fetchBadges, evaluateBookBadges } = useBadges(user?.id);

  useEffect(() => {
    if (user?.id) {
      fetchLivros();
      fetchBadges();
    }
    buscarLivroDoMes().then(dados => {
      setLivro(dados || null);
      setLoading(false);
    });
  }, [user?.id]); // eslint-disable-line

  const status = livro ? getStatusLivro(livro.mes) : 'nao_iniciado';

  const handleIniciar = async () => {
    await iniciarLeitura(livro);
    if (user?.id) {
      await evaluateBookBadges({ livrosIniciados: stats.livrosIniciados + 1, livrosConcluidos: stats.livrosConcluidos });
    }
  };

  const handleConcluir = async () => {
    await marcarComoLido(livro);
    if (user?.id) {
      await evaluateBookBadges({ livrosIniciados: stats.livrosIniciados, livrosConcluidos: stats.livrosConcluidos + 1 });
    }
  };

  if (loading || loadingLivros) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!livro) return null;

  return (
    <div className={`livro-mes-card${status === 'concluido' ? ' concluido' : ''}`}>
      <div className="livro-mes-label">
        <IconBook />
        Livro do Mês
      </div>

      <div className="livro-mes-body">
        {livro.url_capa && !imgErro ? (
          <img
            className="livro-mes-capa"
            src={livro.url_capa}
            alt={livro.titulo}
            onError={() => setImgErro(true)}
          />
        ) : (
          <div className="livro-mes-capa-fallback">
            <IconBookFallback />
          </div>
        )}

        <div className="livro-mes-info">
          <p className="livro-mes-titulo">{livro.titulo}</p>
          <p className="livro-mes-autor">{livro.autor}</p>

          {status === 'concluido' && (
            <span className="livro-mes-badge-leu">
              <IconStar /> Você leu
            </span>
          )}

          {livro.topico && (
            <span className="livro-mes-tag">{livro.topico}</span>
          )}
        </div>
      </div>

      {status === 'nao_iniciado' && livro.sinopse && (
        <p className="livro-mes-sinopse">{livro.sinopse}</p>
      )}

      {status === 'lendo' && (
        <p className="livro-mes-status-lendo">
          <IconCheck /> Você está lendo este livro
        </p>
      )}

      {status === 'concluido' && (
        <p className="livro-mes-status-concluido">
          Parabéns! Mais um passo na sua jornada.
        </p>
      )}

      <div className="livro-mes-actions">
        {livro.link_amazon && (
          <button
            className="btn btn-ghost"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => window.open(livro.link_amazon, '_blank')}
          >
            <IconExternalLink />
            {status === 'concluido' ? 'Ver na Amazon' : 'Comprar na Amazon'}
          </button>
        )}

        {status === 'nao_iniciado' && (
          <button
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={handleIniciar}
          >
            <IconBookOpen /> Começar leitura
          </button>
        )}

        {status === 'lendo' && (
          <button
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={handleConcluir}
          >
            <IconCheck /> Marcar como lido
          </button>
        )}
      </div>
    </div>
  );
}
